import { TextCodes } from '@api/types/translation/TextCodes';
import {
  BlockContainer,
  Button,
  DesktopWishlistLineItem,
  LineItemColumnNames,
  Title,
  WishlistCell,
} from '@components';
import { MobileWishlistLineItem } from '@components/common/commerce/WishlistLineItem/MobileWishlistLineItem';
import { useWishlist } from '@hooks';
import { useCart } from '@hooks/cart/cart.hooks';
import { Alert, Box, styled } from '@mui/material';
import { breakpoints, palette } from '@themes/theme.helpers';
import { t } from '@translations';

const WishlistBlockWrapper = styled(Box)``;

type WishlistBlockProps = {
  title?: string;
};
const CartHeaderBox = styled(Box)`
  display: none;
  margin: 4px 0;
  ${breakpoints.up('lg')} {
    margin: 16px 0;
    border-bottom: 2px solid ${palette.primary('main')};
    display: block;
  }

  .columns {
    background: transparent;
    border: 0;
    padding: 0;
    & > div {
      height: auto;
      font-family: 'Lato';
      font-style: normal;
      font-weight: 700;
      font-size: 16px;
      line-height: 140%;
      letter-spacing: 0.01em;
      color: #1c1c1c;
    }
  }
`;
export const WishlistBlock = ({ title }: WishlistBlockProps) => {
  const { wishlist, getWishlistStatus } = useWishlist();
  const { isError, isFetching, isLoading, isSuccess } = getWishlistStatus;
  const isWorking = isFetching || isLoading;
  const isEmpty = isSuccess && !isWorking && wishlist?.length === 0;
  const { addItemsToCart, addItemsToCartStatus } = useCart();
  const { isLoading: isCartStatusLoading } = addItemsToCartStatus;

  const addFavoritesToCartHandler = () => {
    if (!isEmpty && wishlist) {
      addItemsToCart(
        wishlist.map((item) => ({
          code: item.code || '',
          productName: item.name || '',
          quantity: item.quantity || 0,
        }))
      );
    }
  };

  return (
    <BlockContainer>
      <WishlistBlockWrapper className='wishlist-block'>
        {title && <Title>{title}</Title>}
        {!isEmpty && !isError && isSuccess && <Header />}
        {isError && <Alert severity='error'>{t(TextCodes.Product.Wishlist.Error)}</Alert>}
        {!isError && isEmpty && (
          <Alert severity='info'>{t(TextCodes.Product.Wishlist.Empty)}</Alert>
        )}
        <Box
          sx={{
            display: 'grid',
            gridAutoRows: '1fr',
            gap: 2,
          }}
        >
          {wishlist?.map((item) => (
            <Box key={item.code} sx={{ height: '100%' }}>
              <MobileWishlistLineItem lineItem={item} />
              <DesktopWishlistLineItem lineItem={item} />
            </Box>
          ))}
        </Box>
        {!isEmpty && !isError && isSuccess && wishlist && (
          <Button
            busy={isCartStatusLoading}
            variant='primary'
            onClick={addFavoritesToCartHandler}
            sx={{ mt: 4 }}
          >
            {t(TextCodes.Product.Wishlist.AddAllToCart)}
          </Button>
        )}
      </WishlistBlockWrapper>
    </BlockContainer>
  );
};

const Header = () => {
  return (
    <CartHeaderBox>
      <LineItemColumnNames className='columns' sx={{ mt: 4 }}>
        <WishlistCell></WishlistCell>
        <WishlistCell className='long'>{t(TextCodes.Product.Wishlist.Product)}</WishlistCell>
        <WishlistCell>{t(TextCodes.Product.Wishlist.Price)}</WishlistCell>
        <WishlistCell>{t(TextCodes.Product.Cart.Quantity)}</WishlistCell>
        <WishlistCell></WishlistCell>
      </LineItemColumnNames>
    </CartHeaderBox>
  );
};
